"use client";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/page-header";
import { PanelLayout } from "@/components/ui/panel-layout";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  MarkdownPreviewContent,
  MarkdownPreviewToggle,
  type MarkdownPreviewMode,
} from "@/components/markdown-preview";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TimezoneSelect } from "@/components/ui/timezone-select";
import { ApiKeysSection } from "@/components/profile-api-keys-section";
import { useMcpFlag } from "@/components/mcp-flag-provider";
import { useGlobalModels } from "@/hooks/use-global-models";
import { useUserSettings } from "@/hooks/use-user-settings";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { Session } from "@supabase/supabase-js";
import { BookOpenTextIcon, ChevronRightIcon, LogOutIcon, CloudUploadIcon, FileTextIcon, CheckIcon, UserPenIcon, InfoIcon, PanelLeftOpen, PanelLeftClose, TriangleAlertIcon, XIcon } from "lucide-react";
import {
  BicepsFlexedIcon,
  type BicepsFlexedIconHandle,
} from "@/components/ui/biceps-flexed";
import { FeatherIcon } from "@/components/ui/feather";
import { HomeIcon } from "@/components/ui/home";
import { RocketIcon, type RocketIconHandle } from "@/components/ui/rocket";
import {
  TelescopeIcon,
  type TelescopeIconHandle,
} from "@/components/ui/telescope";
import { WrenchIcon, type WrenchIconHandle } from "@/components/ui/wrench";
import { levelProgress, type GamificationStats } from "@/lib/gamification";
import { useTheme } from "next-themes";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// ── Theme picker ──────────────────────────────────────────────────────────────

type ThemeOption = "light" | "system" | "dark";

function ThemePicker({ value, onChange }: { value: ThemeOption; onChange: (v: ThemeOption) => void }) {
  const options: { id: ThemeOption; label: string }[] = [
    { id: "light",  label: "Light" },
    { id: "system", label: "Auto" },
    { id: "dark",   label: "Dark" },
  ];

  // card: 96x64px. Each theme renders inline — no nested components.
  function renderCard(id: ThemeOption) {
    const lBg = "#ffffff", lLine = "#e2e8f0", lInput = "#f1f5f9", lInputB = "#e2e8f0";
    const dBg = "#1c1c1e", dLine = "#3a3a3c", dInput = "#2c2c2e", dInputB = "#48484a";

    const col = (bg: string, line: string, input: string, inputB: string, w = "100%") => (
      <div style={{ width: w, height: "100%", background: bg, padding: 6, display: "flex", flexDirection: "column", gap: 3, boxSizing: "border-box", overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div style={{ height: 8, width: 36, borderRadius: 9999, background: line }} />
        </div>
        <div style={{ height: 5, width: 28, borderRadius: 9999, background: line }} />
        <div style={{ height: 5, width: 22, borderRadius: 9999, background: line }} />
        <div style={{ marginTop: "auto", height: 14, borderRadius: 5, background: input, border: `1px solid ${inputB}` }} />
      </div>
    );

    if (id === "light") return col(lBg, lLine, lInput, lInputB);
    if (id === "dark")  return col(dBg, dLine, dInput, dInputB);
    // Auto: two equal columns side by side
    return (
      <div style={{ width: "100%", height: "100%", display: "flex" }}>
        {col(lBg, lLine, lInput, lInputB, "50%")}
        {col(dBg, dLine, dInput, dInputB, "50%")}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 16 }}>
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0 }}
        >
          <div style={{
            width: 96, height: 64, borderRadius: 12, overflow: "hidden",
            border: `2px solid ${value === opt.id ? "hsl(var(--primary))" : "hsl(var(--border))"}`,
            transition: "border-color 0.15s",
          }}>
            {renderCard(opt.id)}
          </div>
          <span style={{ fontSize: 12, color: value === opt.id ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))", fontWeight: value === opt.id ? 500 : 400 }}>
            {opt.label}
          </span>
        </button>
      ))}
    </div>
  );
}

// ── Setting row ───────────────────────────────────────────────────────────────

function SettingRow({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-6 py-4 border-b border-border last:border-0">
      <div className="space-y-0.5 min-w-0">
        <Label className="text-sm font-medium text-foreground">{label}</Label>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      </div>
      <div className="shrink-0 pt-0.5">{children}</div>
    </div>
  );
}

// ── CV Upload ─────────────────────────────────────────────────────────────────

type CvStage = "idle" | "uploading" | "processing" | "done" | "error";

type CvProgressEvent =
  | { type: "status"; message: string }
  | { type: "category_done"; label: string; done: number; total: number }
  | { type: "highlight"; text: string }
  | { type: "complete" }
  | { type: "error"; message: string };

function CvUploadSection({
  onOpenDocumentation,
  session,
}: {
  onOpenDocumentation?: (docId: string) => void;
  session: Session;
}) {
  const [stage, setCvStage] = useState<CvStage>("idle");
  const [fileName, setFileName] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(
    async (f: File) => {
      setFileName(f.name);
      setCvStage("uploading");
      setStatusMessage("Reading CV…");
      setError("");

      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const formData = new FormData();
        formData.append("file", f);
        formData.append("userId", session.user.id);
        if (authSession?.access_token) {
          formData.append("supabaseAccessToken", authSession.access_token);
        }

        const response = await fetch("/api/onboarding/resume", {
          method: "POST",
          body: formData,
        });

        if (!response.ok || !response.body) throw new Error("Upload failed — please try again.");

        setCvStage("processing");
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const event = JSON.parse(line) as CvProgressEvent;
              if (event.type === "status") setStatusMessage(event.message);
              else if (event.type === "complete") setCvStage("done");
              else if (event.type === "error") { setError(event.message); setCvStage("error"); }
            } catch { /* ignore malformed lines */ }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Something went wrong.");
        setCvStage("error");
      }
    },
    [session]
  );

  return (
    <div className="py-4 border-b border-border">
      <Label className="text-sm font-medium text-foreground">CV / Resume</Label>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3 leading-relaxed">
        Upload your CV so the AI can personalise responses based on your background.
      </p>

      {stage === "idle" && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <CloudUploadIcon className="mr-2 size-3.5" />
            Upload CV
          </Button>
          {onOpenDocumentation ? (
            <Button
              className="gap-2 text-muted-foreground"
              onClick={() => onOpenDocumentation("cv-upload")}
              size="sm"
              type="button"
              variant="ghost"
            >
              <BookOpenTextIcon className="size-3.5" />
              Learn more
            </Button>
          ) : null}
        </div>
      )}

      {(stage === "uploading" || stage === "processing") && (
        <div className="flex items-center gap-2">
          <FileTextIcon className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="text-xs text-muted-foreground truncate max-w-48">{fileName}</span>
          <span className="text-xs text-muted-foreground/60">— {statusMessage}</span>
        </div>
      )}

      {stage === "done" && (
        <div className="flex items-center gap-2">
          <CheckIcon className="size-3.5 text-primary" />
          <span className="text-xs text-muted-foreground">{fileName} — CV processed</span>
          <button
            type="button"
            className="ml-auto text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            onClick={() => { setCvStage("idle"); setFileName(""); fileInputRef.current?.click(); }}
          >
            Replace
          </button>
        </div>
      )}

      {stage === "error" && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => { setCvStage("idle"); setError(""); }}>
            Try again
          </Button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.txt"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); e.target.value = ""; }}
      />
    </div>
  );
}

// ── AI Journey link ───────────────────────────────────────────────────────────

type LevelIconHandle =
  | BicepsFlexedIconHandle
  | RocketIconHandle
  | TelescopeIconHandle
  | WrenchIconHandle;

function JourneyLink({ stats }: { stats: GamificationStats }) {
  const router = useRouter();
  const iconRef = useRef<LevelIconHandle>(null);
  const { level, nextLevel, progress, pointsToNext } = levelProgress(stats.lifetime_score);

  const iconProps = { "aria-hidden": true, className: "text-brand", size: 20 } as const;
  const levelIcon =
    level.id === "practitioner" ? <WrenchIcon ref={iconRef} {...iconProps} />
    : level.id === "builder" ? <BicepsFlexedIcon ref={iconRef} {...iconProps} />
    : level.id === "champion" ? <RocketIcon ref={iconRef} {...iconProps} />
    : <TelescopeIcon ref={iconRef} {...iconProps} />;

  return (
    <button
      type="button"
      onClick={() => router.push("/journey")}
      onMouseEnter={() => { try { iconRef.current?.startAnimation(); } catch {} }}
      onMouseLeave={() => { try { iconRef.current?.stopAnimation(); } catch {} }}
      className="group mt-5 flex w-full items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 text-left transition-colors hover:bg-accent/50"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted/50">
        {levelIcon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">
          {level.name}
          <span className="ml-2 text-xs font-normal tabular-nums text-muted-foreground">
            {stats.lifetime_score} journey points
          </span>
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          <div className="h-1 max-w-48 flex-1 overflow-hidden rounded-full bg-border">
            <div
              className="h-full rounded-full bg-brand transition-[width] duration-700 ease-out"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">
            {nextLevel ? `${pointsToNext} to ${nextLevel.name}` : "Max level"}
          </span>
        </div>
      </div>
      <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground transition-colors group-hover:text-foreground">
        <span className="hidden sm:inline">View journey</span>
        <ChevronRightIcon className="size-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

// ── Sections ──────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: "general",  label: "General" },
  { id: "ai-tools", label: "AI & Tools" },
  { id: "api-keys", label: "API Keys" },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

// ── Panel ─────────────────────────────────────────────────────────────────────

type Profile = {
  full_name: string | null;
  display_name: string;
  username: string;
  bio: string;
  job_role: string | null;
  industry: string | null;
  profile_context: string | null;
  writing_style: string | null;
};

type ProfilePanelProps = {
  session: Session;
  onBack: () => void;
  onOpenDocumentation?: (docId: string) => void;
};

export function ProfilePanel({ session, onBack, onOpenDocumentation }: ProfilePanelProps) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [personalTeamId, setPersonalTeamId] = useState<string | null>(null);
  const [journeyStats, setJourneyStats] = useState<GamificationStats | null>(null);
  const [activeSection, setActiveSection] = useState<SectionId>("general");
  const mcpEnabled = useMcpFlag();
  const visibleSections = mcpEnabled ? SECTIONS : SECTIONS.filter((s) => s.id !== "api-keys");
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const currentTheme = (theme === "light" || theme === "dark" ? theme : "system") as ThemeOption;
  const { settings, setModel, setTool, setResolveDateTimeDefaultTimezone } = useUserSettings(session.user.id);
  const [preferencesText, setPreferencesText] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editFullName, setEditFullName] = useState("");
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [wsPopoverOpen, setWsPopoverOpen] = useState(false);
  const [wsMode, setWsMode] = useState<MarkdownPreviewMode>("preview");
  const [wsEditText, setWsEditText] = useState("");
  const [wsSaving, setWsSaving] = useState(false);
  const { models: globalModels } = useGlobalModels();

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const [profileResult, personalTeamResult, statsResult] = await Promise.all([
        supabase
          .from("profiles")
          .select("full_name, display_name, username, bio, job_role, industry, profile_context, writing_style")
          .eq("user_id", session.user.id)
          .single(),
        supabase
          .from("teams")
          .select("id")
          .eq("user_id", session.user.id)
          .eq("is_home", true)
          .eq("team_type", "personal")
          .maybeSingle(),
        supabase.rpc("get_my_gamification_stats", {
          p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      ]);

      if (cancelled) return;

      if (profileResult.data) {
        setProfile(profileResult.data as Profile);
        setPreferencesText(profileResult.data.profile_context ?? "");
      }

      if (personalTeamResult.error) {
        console.error("[ProfilePanel] failed to load personal Home:", personalTeamResult.error);
      } else {
        setPersonalTeamId(personalTeamResult.data?.id ?? null);
      }

      // Journey stats are a nice-to-have here — failure just hides the link.
      if (statsResult.error) {
        console.error("[ProfilePanel] failed to load journey stats:", statsResult.error);
      } else if (statsResult.data) {
        setJourneyStats(statsResult.data as GamificationStats);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session.user.id]);

  const email = session.user.email ?? "";
  const displayName = profile?.full_name || profile?.display_name || email.split("@")[0];

  function openEdit() {
    setEditFullName(profile?.full_name ?? "");
    setEditDisplayName(profile?.display_name ?? "");
    setEditOpen(true);
  }

  async function saveEdit() {
    setEditSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: editFullName, display_name: editDisplayName })
      .eq("user_id", session.user.id);
    setEditSaving(false);
    if (error) {
      toast.error("Failed to save profile");
    } else {
      setProfile((p) => p ? { ...p, full_name: editFullName, display_name: editDisplayName } : p);
      setEditOpen(false);
      toast.success("Profile updated");
    }
  }
  async function saveWritingStyle() {
    setWsSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ writing_style: wsEditText })
      .eq("user_id", session.user.id);
    setWsSaving(false);
    if (error) {
      toast.error("Failed to save writing style");
    } else {
      setProfile((p) => p ? { ...p, writing_style: wsEditText } : p);
      setWsMode("preview");
      toast.success("Writing style saved");
    }
  }

  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const sidebarNav = (
    <nav className="flex-1 space-y-0.5">
      {visibleSections.map((s) => (
        <button
          key={s.id}
          data-testid={`profile-section-${s.id}`}
          onClick={() => { setActiveSection(s.id); setMobileSidebarOpen(false); }}
          className={cn(
            "w-full rounded-lg px-3 py-2 text-sm text-left transition-colors",
            activeSection === s.id
              ? "bg-accent text-accent-foreground font-medium"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          )}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );

  const sidebar = (
    <>
      {sidebarNav}
      {/* Sign out — desktop only; mobile uses the top-right icon */}
      <div className="mt-auto pt-2 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground hover:text-destructive"
          onClick={() => supabase.auth.signOut()}
        >
          <LogOutIcon className="mr-2 size-4" />
          Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
        <SheetContent className="w-52 p-0" side="left">
          <SheetTitle className="sr-only">Profile navigation</SheetTitle>
          <SheetDescription className="sr-only">Profile section links and sign out.</SheetDescription>
          <div className="flex h-full flex-col p-3">{sidebarNav}</div>
        </SheetContent>
      </Sheet>

      {/* Mobile top-right actions — sidebar toggle + sign out */}
      <div className="absolute right-0 top-0 z-10 flex items-center gap-0.5 p-2 sm:hidden">
        <Button
          aria-label={mobileSidebarOpen ? "Close navigation" : "Open navigation"}
          className="h-8 w-8"
          onClick={() => setMobileSidebarOpen((o) => !o)}
          size="icon"
          type="button"
          variant="ghost"
        >
          {mobileSidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </Button>
        <Button
          aria-label="Sign out"
          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => supabase.auth.signOut()}
          size="icon"
          type="button"
          variant="ghost"
        >
          <LogOutIcon size={16} />
        </Button>
      </div>

      <PageHeader
        description="Manage your account details, appearance settings, and session access."
        onBack={onBack}
        title="Profile"
      />

      <PanelLayout sidebar={sidebar} sidebarClassName="hidden sm:flex">
        {activeSection === "general" && (
          <>
            {/* Identity card */}
            <div className="rounded-xl border border-border bg-muted/30 px-6 py-5 mb-8 relative">
              <button
                type="button"
                onClick={openEdit}
                className="absolute top-4 right-4 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                aria-label="Edit profile"
              >
                <UserPenIcon className="size-3.5" />
              </button>
              <div className="flex items-center gap-5">
                <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-foreground text-background text-xl font-semibold tracking-tight">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold text-foreground leading-tight">{displayName}</p>
                  {profile?.display_name && profile.display_name !== displayName && (
                    <p className="text-sm text-muted-foreground mt-0.5">{profile.display_name}</p>
                  )}
                  <p className="text-sm text-muted-foreground my-0.5">{email}</p>
                  {profile?.job_role && (
                    <span className="rounded-full border border-border bg-background px-2 py-1 text-xs text-muted-foreground">
                      {profile.job_role}
                    </span>
                  )}
                </div>
              </div>
              {journeyStats && <JourneyLink stats={journeyStats} />}
            </div>

            <h2 className="text-base font-semibold text-foreground mb-1">General</h2>
            <p className="text-xs text-muted-foreground mb-6">Appearance, session, and personal context settings.</p>

            <SettingRow
              description="Open your private workspace, files, agents, and activity."
              label="Personal Home"
            >
              <Button
                disabled={!personalTeamId}
                onClick={() => personalTeamId && router.push(`/teams/${personalTeamId}`)}
                size="sm"
                type="button"
                variant="outline"
              >
                <HomeIcon size={14} />
                Open Home
              </Button>
            </SettingRow>

            {/* Personal preferences */}
            <div className="py-4 border-b border-border">
              <Label className="text-sm font-medium text-foreground">
                What personal context should the AI consider in responses?
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-3 leading-relaxed">
                Your preferences will apply to all conversations. e.g. preferred tone, background, communication style.
              </p>
              <Textarea
                className="min-h-[120px] resize-y text-sm"
                placeholder="e.g. ask clarifying questions before giving detailed answers"
                value={preferencesText}
                onChange={(e) => setPreferencesText(e.target.value)}
                onBlur={() => {
                  supabase
                    .from("profiles")
                    .update({ profile_context: preferencesText })
                    .eq("user_id", session.user.id)
                    .then(({ error }) => {
                      if (error) {
                        console.error("[profile-panel] profile_context save error:", error.message);
                        toast.error("Failed to save context");
                      } else {
                        toast.success("Context saved");
                      }
                    });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    supabase
                      .from("profiles")
                      .update({ profile_context: preferencesText })
                      .eq("user_id", session.user.id)
                      .then(({ error }) => {
                        if (error) {
                          console.error("[profile-panel] profile_context save error:", error.message);
                          toast.error("Failed to save context");
                        } else {
                          toast.success("Personal preferences saved");
                        }
                      });
                  }
                }}
              />
            </div>

            <CvUploadSection
              onOpenDocumentation={onOpenDocumentation}
              session={session}
            />

            <div className="py-4 border-b border-border">
              <Label className="text-sm font-medium text-foreground">Writing Style</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-3 leading-relaxed">
                Share a few of your best written pieces and the AI will learn to write in your voice.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => router.push("/onboarding/writing-style")}
                >
                  <FeatherIcon className="mr-2 size-3.5" />
                  Update writing style
                </Button>
                {onOpenDocumentation ? (
                  <Button
                    className="gap-2 text-muted-foreground"
                    onClick={() => onOpenDocumentation("writing-style-profile")}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    <BookOpenTextIcon className="size-3.5" />
                    Learn more
                  </Button>
                ) : null}
                {profile && !profile.writing_style && (
                  <span className="flex items-center gap-1 text-xs text-amber-500">
                    <TriangleAlertIcon className="size-3.5 shrink-0" />
                    not set
                  </span>
                )}
                {profile?.writing_style && (
                  <>
                    <CheckIcon className="size-3.5 shrink-0 text-green-500" />
                    <Dialog
                      open={wsPopoverOpen}
                      onOpenChange={(open) => {
                        setWsPopoverOpen(open);
                        if (open) {
                          setWsEditText(profile?.writing_style ?? "");
                          setWsMode("preview");
                        } else {
                          setWsEditText("");
                          setWsMode("preview");
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground">
                          <InfoIcon className="size-3.5" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="flex max-h-[90dvh] w-full max-w-[calc(100%-3rem)] flex-col gap-0 p-0 sm:max-w-2xl" showCloseButton={false}>
                        <DialogHeader className="flex flex-row items-center justify-between border-b border-border px-6 py-4">
                          <DialogTitle>Your writing style</DialogTitle>
                          <div className="flex items-center gap-2">
                            <MarkdownPreviewToggle mode={wsMode} onModeChange={setWsMode} />
                            <DialogClose asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
                                <XIcon className="size-4" />
                              </Button>
                            </DialogClose>
                          </div>
                        </DialogHeader>
                        <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                          <MarkdownPreviewContent
                            mode={wsMode}
                            value={wsEditText}
                            onChange={setWsEditText}
                            emptyPreviewText="No writing style saved yet."
                            textareaClassName="h-full min-h-[26rem]"
                            previewClassName="h-full min-h-[26rem] border-none bg-transparent px-0 py-0"
                          />
                        </div>
                        {wsMode === "markdown" && (
                          <DialogFooter className="border-t border-border px-6 py-4">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setWsEditText(profile?.writing_style ?? "");
                                setWsMode("preview");
                              }}
                            >
                              Cancel
                            </Button>
                            <Button onClick={saveWritingStyle} disabled={wsSaving}>
                              {wsSaving ? "Saving…" : "Save"}
                            </Button>
                          </DialogFooter>
                        )}
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </div>

            <div className="py-4 border-b border-border">
              <Label className="text-sm font-medium text-foreground">Appearance</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-4 leading-relaxed">Choose your preferred interface theme.</p>
              <ThemePicker value={currentTheme} onChange={setTheme} />
            </div>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit profile</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-full-name">Full name</Label>
                    <Input
                      id="edit-full-name"
                      value={editFullName}
                      onChange={(e) => setEditFullName(e.target.value)}
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-display-name">Display name</Label>
                    <Input
                      id="edit-display-name"
                      value={editDisplayName}
                      onChange={(e) => setEditDisplayName(e.target.value)}
                      placeholder="@handle"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-email" className="text-muted-foreground">Email</Label>
                    <Input
                      id="edit-email"
                      value={email}
                      disabled
                      className="text-muted-foreground"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                  <Button onClick={saveEdit} disabled={editSaving}>
                    {editSaving ? "Saving…" : "Save"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {activeSection === "ai-tools" && (
          <>
            <h2 className="text-base font-semibold text-foreground mb-1">AI &amp; Tools</h2>
            <p className="text-xs text-muted-foreground mb-6">Default model and tool preferences for new chats.</p>

            <SettingRow
              label="Default model"
              description="The AI model used for new chats. You can still switch models per conversation."
            >
              <Select
                value={settings.model}
                onValueChange={(v) => setModel(v as typeof settings.model)}
              >
                <SelectTrigger data-testid="profile-default-model" className="w-auto min-w-40 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {globalModels
                    .filter((m) => m.is_active)
                    .map((m) => (
                    <SelectItem key={m.id} value={m.model_id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow
              label="Web search"
              description="Allow the assistant to search the web for up-to-date information when answering questions."
            >
              <Switch
                data-testid="profile-web-search"
                checked={settings.tools.webSearch}
                onCheckedChange={(v) => setTool("webSearch", v)}
              />
            </SettingRow>

            <SettingRow
              label="Web fetch"
              description="Allow the assistant to fetch content from specific URLs when answering questions."
            >
              <Switch
                checked={settings.tools.webFetch}
                onCheckedChange={(v) => setTool("webFetch", v)}
              />
            </SettingRow>

            <SettingRow
              label="Memory"
              description="Let the assistant remember facts about you across conversations to give more personalised responses."
            >
              <Switch
                checked={settings.tools.memory}
                onCheckedChange={(v) => setTool("memory", v)}
              />
            </SettingRow>

            <SettingRow
              label="Code execution"
              description="Allow the assistant to run code in a sandbox to analyse data, process files, and generate documents. Enabled by default; can also be toggled per chat."
            >
              <Switch
                checked={settings.tools.codeExecution}
                onCheckedChange={(v) => setTool("codeExecution", v)}
              />
            </SettingRow>

            <SettingRow
              label="My files"
              description="Allow the assistant to list and search your own uploaded Library files directly in chat, without needing an agent."
            >
              <Switch
                checked={settings.tools.myFiles}
                onCheckedChange={(v) => setTool("myFiles", v)}
              />
            </SettingRow>

            <SettingRow
              label="Writing style profile"
              description="Allow the assistant to load your saved writing style when you ask it to write in your own voice."
            >
              <Switch
                checked={settings.tools.writingStyleProfile}
                onCheckedChange={(v) => setTool("writingStyleProfile", v)}
              />
            </SettingRow>

            <div className="flex items-start justify-between gap-6 py-4 border-b border-border last:border-0">
              <div className="space-y-0.5 min-w-0">
                <Label className="text-sm font-medium text-foreground">Resolve date &amp; time</Label>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Allow the assistant to resolve natural-language date phrases (e.g. today, tomorrow, next Monday) in NZ time and UTC when requested.
                </p>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-2 pt-0.5">
                <Switch
                  checked={settings.tools.resolveDateTime}
                  onCheckedChange={(v) => setTool("resolveDateTime", v)}
                />
                <TimezoneSelect
                  value={settings.resolveDateTimeConfig.defaultTimezone}
                  onChange={setResolveDateTimeDefaultTimezone}
                />
              </div>
            </div>
          </>
        )}

        {mcpEnabled && activeSection === "api-keys" && (
          <ApiKeysSection accessToken={session.access_token} />
        )}
      </PanelLayout>
    </div>
  );
}
